import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_URL } from '../../config/api';
import { getCurrentUser } from '../../utils/auth';
import { WIG_OPTIONS, DEFAULT_WIG_VALUES, getColorOptions } from '../../constants/wigOptions';
import {
  STOCK_WIG_ROOMS,
  hasRachelsRoomAccess,
  formatStockWigOrderNo
} from '../../constants/rachelsRoomConfig';
import EditableCell from '../EditableCell';
import StockWigWorksheet from './StockWigWorksheet/StockWigWorksheet';
import ShippingPrintView from './ShippingPrintView/ShippingPrintView';
import ReturnShippingPrintView from './ReturnShippingPrintView/ReturnShippingPrintView';
import './RachelsRoom.css';

const RachelsRoom = () => {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  // Check permissions
  useEffect(() => {
    if (!hasRachelsRoomAccess(currentUser)) {
      alert('You do not have access to Rachel\'s Room');
      navigate('/');
    }
  }, [currentUser, navigate]);

  // Wig Info State (Filter/Selection) - Initialize with defaults
  const [wigInfo, setWigInfo] = useState({
    stylist: DEFAULT_WIG_VALUES?.CutByStyle || 'DiNi',
    baseColor: DEFAULT_WIG_VALUES?.Base_Color || 'Dark',
    wigType: DEFAULT_WIG_VALUES?.Wig_Type || 'Standard Lace Top',
    wigLength: DEFAULT_WIG_VALUES?.Wig_Length || 'Short',
    netSize: DEFAULT_WIG_VALUES?.Net_Size || 'XS',
    hairType: DEFAULT_WIG_VALUES?.Hair_Type || 'Wavy',
    netType: DEFAULT_WIG_VALUES?.Net_Type || 'Deep Net',
    laceType: DEFAULT_WIG_VALUES?.Lace_Type || 'No Cut',
    topSize: DEFAULT_WIG_VALUES?.Top_Size || '6"x4" 100%',
    stockType: DEFAULT_WIG_VALUES?.InventoryType || 'Stock'
  });

  // Customer Order Search State
  const [customerOrderSearch, setCustomerOrderSearch] = useState('');
  const [customerOrders, setCustomerOrders] = useState([]);
  const [loadingCustomerOrders, setLoadingCustomerOrders] = useState(false);

  const [showWigTypeOther, setShowWigTypeOther] = useState(false);
  const [wigTypeOther, setWigTypeOther] = useState('');
  const [showTopSizeOther, setShowTopSizeOther] = useState(false);
  const [topSizeOther, setTopSizeOther] = useState('');

  // New Wig Entry State
  const [newWig, setNewWig] = useState({
    barcode: '',
    topCode: '',
    wigWeight: '',
    comments: ''
  });

  // Inventory State
  const [inventory, setInventory] = useState([]);
  const [selectedWig, setSelectedWig] = useState(null);
  const [roomTasks, setRoomTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedForShipping, setSelectedForShipping] = useState([]);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const itemsPerPage = 25;

  // Search State
  const [searchBarcode, setSearchBarcode] = useState('');

  // Worksheet State
  const [showWorksheet, setShowWorksheet] = useState(false);

  // Shipping State
  const [shipments, setShipments] = useState([]);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [shipmentWigs, setShipmentWigs] = useState([]);
  const [loadingShipments, setLoadingShipments] = useState(false);
  const [showShippingPrint, setShowShippingPrint] = useState(false);
  const [sentDate, setSentDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [exporting, setExporting] = useState(false);

  // Shipment Pagination State
  const [shipmentPage, setShipmentPage] = useState(1);
  const [shipmentTotalPages, setShipmentTotalPages] = useState(1);
  const [shipmentTotalRecords, setShipmentTotalRecords] = useState(0);
  const shipmentsPerPage = 20;

  // Transfer State
  const [availableTransferRooms, setAvailableTransferRooms] = useState([]);
  const [selectedTransferRoom, setSelectedTransferRoom] = useState('');
  const [currentRoomInfo, setCurrentRoomInfo] = useState(null);
  const [transferring, setTransferring] = useState(false);

  // Return Shipment State
  const [returnForm, setReturnForm] = useState({
    shippingToName: 'Select Location',
    courrierName: 'Driver',
    trackingNumber: '',
    specialInstructions: ''
  });
  const [returnBarcodeSearch, setReturnBarcodeSearch] = useState('');
  const [returnItems, setReturnItems] = useState([]);
  const [loadingReturnSearch, setLoadingReturnSearch] = useState(false);
  const [creatingReturn, setCreatingReturn] = useState(false);
  const [showReturnPrintView, setShowReturnPrintView] = useState(false);
  const [lastCreatedReturnId, setLastCreatedReturnId] = useState(null);
  const [wigsNotInPossession, setWigsNotInPossession] = useState([]);
  const [showWigsNotInPossession, setShowWigsNotInPossession] = useState(false);
  const [loadingWigsNotInPossession, setLoadingWigsNotInPossession] = useState(false);
  const [wigsNotInPossessionSearch, setWigsNotInPossessionSearch] = useState('');

  // Fetch inventory on mount and when page or search changes
  useEffect(() => {
    fetchInventory();
  }, [currentPage, searchBarcode]);

  // Fetch shipments on mount and when shipment page changes
  useEffect(() => {
    fetchShipments();
  }, [shipmentPage]);


  const fetchInventory = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/api/rachels-room/inventory?page=${currentPage}&limit=${itemsPerPage}`;
      if (searchBarcode) {
        url += `&search=${encodeURIComponent(searchBarcode)}`;
      }
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch inventory');
      const result = await response.json();
      setInventory(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalRecords(result.pagination.total);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      alert('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomTasks = async (orderNo) => {
    try {
      const response = await fetch(`${API_URL}/api/rachels-room/room-tasks/${orderNo}`);
      if (!response.ok) throw new Error('Failed to fetch room tasks');
      const data = await response.json();

      // Debug logging
      console.log('Room tasks before filter:', data.map(t => ({
        roomNo: t.RoomNo,
        type: typeof t.RoomNo,
        id: t.ID
      })));

      // Filter out room 9001 (prep room should never be shown to users)
      const filteredTasks = data.filter(task => String(task.RoomNo) !== '9001');

      console.log('Room tasks after filter:', filteredTasks.length, 'tasks');

      setRoomTasks(filteredTasks);
    } catch (error) {
      console.error('Error fetching room tasks:', error);
      alert('Failed to load room tasks');
    }
  };

  const fetchShipments = async () => {
    try {
      setLoadingShipments(true);
      const response = await fetch(`${API_URL}/api/rachels-room/shipments?page=${shipmentPage}&limit=${shipmentsPerPage}`);
      if (!response.ok) throw new Error('Failed to fetch shipments');
      const result = await response.json();
      setShipments(result.data);
      setShipmentTotalPages(result.pagination.totalPages);
      setShipmentTotalRecords(result.pagination.total);
    } catch (error) {
      console.error('Error fetching shipments:', error);
      toast.error('Failed to load shipments');
    } finally {
      setLoadingShipments(false);
    }
  };

  const fetchShipmentDetails = async (shipmentId) => {
    try {
      const response = await fetch(`${API_URL}/api/rachels-room/shipping-print/${shipmentId}`);
      if (!response.ok) throw new Error('Failed to fetch shipment details');
      const data = await response.json();
      setShipmentWigs(data.wigs || []);
      setSelectedShipment(data);
    } catch (error) {
      console.error('Error fetching shipment details:', error);
      toast.error('Failed to load shipment details');
      setShipmentWigs([]);
      setSelectedShipment(null);
    }
  };

  const handleSelectShipment = async (shipment) => {
    await fetchShipmentDetails(shipment.shipmentId);
  };

  const handlePrintShipping = () => {
    if (!selectedShipment) {
      toast.error('Please select a shipment first');
      return;
    }
    // Check if this is a return shipment (ShipOutDate year < 1901)
    if (selectedShipment.shipmentDetails.ShipOutDate &&
        new Date(selectedShipment.shipmentDetails.ShipOutDate).getFullYear() < 1901) {
      // Show return print view for received shipments
      setLastCreatedReturnId(selectedShipment.shipmentDetails.shipmentId);
      setShowReturnPrintView(true);
    } else {
      // Show regular shipping print view
      setShowShippingPrint(true);
    }
  };

  // Export shipment to Google Sheets
  const handleExportToSheets = async () => {
    if (!selectedShipment || !shipmentWigs || shipmentWigs.length === 0) {
      toast.error('Please select a shipment with wigs first');
      return;
    }

    try {
      setExporting(true);

      const shipDetails = selectedShipment.shipmentDetails;
      const sheetData = [];

      // Add shipment header info starting at column B (all rows must have 6 columns)
      sheetData.push(['', 'Shipping Order No:', shipDetails.shipmentId || '', '', '', '']);
      sheetData.push(['', 'Ship Out Date:', shipDetails.ShipOutDate
        ? (new Date(shipDetails.ShipOutDate).getFullYear() < 1901
          ? 'Received'
          : new Date(shipDetails.ShipOutDate).toLocaleDateString())
        : 'Not Sent', '', '', '']);
      sheetData.push(['', 'Courier:', shipDetails.CourrierName || '', '', '', '']);
      sheetData.push(['', 'Tracking Number:', shipDetails.TrackingNumber || '', '', '', '']);
      sheetData.push(['', 'Total Weight:', `${selectedShipment.totalWeight || 0} g`, '', '', '']);
      sheetData.push(['', '', '', '', '', '']); // Empty row as separator

      // Column headers at row 7 - reordered: Wig Type, Weight, Customer, Instructions, Barcode
      // Adding 'HEADER_ROW' marker in first position to signal Google Apps Script to style this row
      const headers = ['#', 'Wig Type', 'Weight (g)', 'Customer', 'Instructions', 'Barcode'];
      sheetData.push(headers);

      // Add wig rows with reordered columns
      shipmentWigs.forEach((wig) => {
        sheetData.push([
          wig.Number || '',
          wig.OrderWigType || '',
          wig.WigWeight ? parseFloat(wig.WigWeight).toFixed(3) : '0',
          wig.OrderCustomerName || '',
          wig.ShippingInstructions || '',
          wig.Wig_Bar_Code || ''
        ]);
      });

      // Ensure all rows have exactly 6 columns (pad or trim)
      const normalizedData = sheetData.map(row => {
        const normalized = [...row];
        while (normalized.length < 6) normalized.push('');
        return normalized.slice(0, 6);
      });

      // Convert to CSV format (strip newlines/carriage returns from cell values
      // because the Google Apps Script parseCsv splits on \n before handling quotes)
      const csvRows = normalizedData.map(row =>
        row.map(cell => `"${String(cell || '').replace(/"/g, '""').replace(/[\r\n]+/g, ' ')}"`).join(',')
      ).join('\n');

      // Rachel's Room dedicated Google Apps Script URL
      const RACHELS_ROOM_SHEETS_URL = 'https://script.google.com/macros/s/AKfycbw21jT5hSWQ25dEuO12Mqg-POlyhi4KqtcbBIwe-8QMPAfmzrDpI_UcWGXRw6IqxQ/exec';

      // Create form and submit to Google Apps Script
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = RACHELS_ROOM_SHEETS_URL;
      form.target = '_blank';
      form.style.display = 'none';

      // Build report name
      const shipDate = selectedShipment.shipmentDetails.EntryDate
        ? new Date(selectedShipment.shipmentDetails.EntryDate).toLocaleDateString().replace(/\//g, '-')
        : new Date().toLocaleDateString().replace(/\//g, '-');
      const reportName = `Shipment_${selectedShipment.shipmentDetails.shipmentId}_${selectedShipment.shipmentDetails.ShippingToName || 'Unknown'}_${shipDate}`;

      // Add roomNo parameter (required by Google Apps Script) - use report name
      const roomInput = document.createElement('input');
      roomInput.type = 'hidden';
      roomInput.name = 'roomNo';
      roomInput.value = reportName;
      form.appendChild(roomInput);

      // Add data parameter (required by Google Apps Script) - CSV data
      const dataInput = document.createElement('input');
      dataInput.type = 'hidden';
      dataInput.name = 'data';
      dataInput.value = csvRows;
      form.appendChild(dataInput);

      document.body.appendChild(form);
      form.submit();

      setTimeout(() => {
        document.body.removeChild(form);
      }, 1000);

      toast.success('Export initiated - check the new browser tab');
    } catch (err) {
      console.error('Error exporting to Google Sheets:', err);
      toast.error('Failed to export to Google Sheets');
    } finally {
      setExporting(false);
    }
  };

  // Shipping Form State
  const [shippingForm, setShippingForm] = useState({
    shippingToName: 'Select Location',
    trackingNumber: '',
    specialInstructions: '',
    courrierName: 'Driver'
  });
  const [creatingShipment, setCreatingShipment] = useState(false);

  const handleCreateShipment = async () => {
    // Validation
    if (shippingForm.shippingToName === 'Select Location') {
      toast.error('Please select a shipping location');
      return;
    }
    if (!shippingForm.trackingNumber.trim()) {
      toast.error('Tracking number is required');
      return;
    }
    if (selectedForShipping.length === 0) {
      toast.error('No wigs selected');
      return;
    }

    try {
      setCreatingShipment(true);
      const response = await fetch(`${API_URL}/api/rachels-room/create-shipment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shippingToName: shippingForm.shippingToName,
          trackingNumber: shippingForm.trackingNumber,
          specialInstructions: shippingForm.specialInstructions,
          courrierName: shippingForm.courrierName,
          selectedBarcodes: selectedForShipping,
          createdBy: currentUser?.username || 'System'
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create shipment');
      }

      const result = await response.json();
      toast.success(`${result.message} - ${result.wigsShipped} wigs shipped, ${result.transferredCount ?? 0} transferred`);

      if (result.transferWarnings && result.transferWarnings.length > 0) {
        const warningMsg = `${result.transferWarnings.length} wig(s) could not be transferred to room:\n` +
          result.transferWarnings.join('\n');
        toast.warning(warningMsg, { autoClose: 10000 });
        console.warn('Transfer warnings:', result.transferWarnings);
      }

      // Reset form and refresh data
      setShippingForm({
        shippingToName: 'Select Location',
        trackingNumber: '',
        specialInstructions: '',
        courrierName: 'Driver'
      });
      setSelectedForShipping([]);
      setCustomerOrders([]); // Clear customer orders
      fetchInventory();
      fetchShipments();
      setShipmentPage(1);
    } catch (error) {
      console.error('Error creating shipment:', error);
      toast.error(error.message);
    } finally {
      setCreatingShipment(false);
    }
  };

  const handleMarkSent = async () => {
    if (!selectedShipment) {
      toast.error('Please select a shipment first');
      return;
    }

    // Check if already sent or is a return shipment
    if (selectedShipment.shipmentDetails.ShipOutDate) {
      const shipOutDate = new Date(selectedShipment.shipmentDetails.ShipOutDate);
      if (shipOutDate.getFullYear() < 1901) {
        toast.error('This is a return shipment - cannot mark as sent');
      } else {
        toast.error(`Shipment was already sent on ${shipOutDate.toLocaleDateString()}`);
      }
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/rachels-room/mark-shipment-sent/${selectedShipment.shipmentDetails.shipmentId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sentDate })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to mark shipment as sent');
      }

      const result = await response.json();
      toast.success(result.message);
      fetchShipments();
      fetchShipmentDetails(selectedShipment.shipmentDetails.shipmentId);
    } catch (error) {
      console.error('Error marking shipment sent:', error);
      toast.error(error.message);
    }
  };

  // Handle weight update for shipment wigs
  const handleUpdateWigWeight = async (barcode, newWeight) => {
    try {
      const weight = parseFloat(newWeight);
      if (isNaN(weight) || weight < 0) {
        toast.error('Please enter a valid weight');
        return;
      }

      const response = await fetch(`${API_URL}/api/rachels-room/update-wig-weight`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          barcode: barcode,
          weight: weight
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update weight');
      }

      const result = await response.json();
      toast.success(`Weight updated to ${weight}g`);

      // Refresh shipment details to show updated weight
      if (selectedShipment) {
        fetchShipmentDetails(selectedShipment.shipmentDetails.shipmentId);
      }

    } catch (error) {
      console.error('Error updating wig weight:', error);
      toast.error(error.message);
    }
  };

  // Handle updating shipment wig fields (barcode, wig type, customer, instructions)
  const handleUpdateShipmentWig = async (barcode, field, newValue) => {
    try {
      const response = await fetch(`${API_URL}/api/rachels-room/update-shipment-wig`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          barcode: barcode,
          field: field,
          value: newValue
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update field');
      }

      toast.success(`${field} updated successfully`);

      // Refresh shipment details to show updated data
      if (selectedShipment) {
        fetchShipmentDetails(selectedShipment.shipmentDetails.shipmentId);
      }

    } catch (error) {
      console.error('Error updating shipment wig:', error);
      toast.error(error.message);
    }
  };

  // Handle updating shipment tracking number
  const handleUpdateShipmentTracking = async (shipmentId, newTrackingNumber) => {
    try {
      const response = await fetch(`${API_URL}/api/rachels-room/update-shipment-tracking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shipmentId: shipmentId,
          trackingNumber: newTrackingNumber
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update tracking number');
      }

      toast.success('Tracking number updated');

      // Refresh shipments list to show updated data
      fetchShipments();

    } catch (error) {
      console.error('Error updating tracking number:', error);
      toast.error(error.message);
    }
  };

  // Handle updating shipment entry date
  const handleUpdateShipmentEntryDate = async (shipmentId, newDate) => {
    try {
      const response = await fetch(`${API_URL}/api/rachels-room/update-shipment-entry-date`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shipmentId: shipmentId,
          entryDate: newDate
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update entry date');
      }

      toast.success('Entry date updated');
      fetchShipments();

    } catch (error) {
      console.error('Error updating entry date:', error);
      toast.error(error.message);
    }
  };

  // Customer Order Search Handler
  const handleCustomerOrderSearch = async () => {
    if (!customerOrderSearch.trim()) {
      toast.error('Please enter an order number');
      return;
    }

    try {
      setLoadingCustomerOrders(true);
      const response = await fetch(`${API_URL}/api/rachels-room/search-customer-order/${customerOrderSearch.trim()}`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Order not found');
      }

      const order = await response.json();

      // Check if already selected
      if (selectedForShipping.includes(order.barcode)) {
        toast.info('This order is already selected for shipping');
        return;
      }

      // Add to customer orders list and selected for shipping
      setCustomerOrders(prev => {
        const exists = prev.find(o => o.barcode === order.barcode);
        if (exists) return prev;
        return [...prev, order];
      });

      setSelectedForShipping(prev => [...prev, order.barcode]);
      toast.success(`Added Order #${order.orderNo} (${order.customerName}) to shipping`);
      setCustomerOrderSearch('');
    } catch (error) {
      console.error('Error searching customer order:', error);
      toast.error(error.message);
    } finally {
      setLoadingCustomerOrders(false);
    }
  };

  const handleRemoveCustomerOrder = (barcode) => {
    setCustomerOrders(prev => prev.filter(o => o.barcode !== barcode));
    setSelectedForShipping(prev => prev.filter(b => b !== barcode));
  };

  // ==================== RETURN SHIPMENT HANDLERS ====================

  // Search for a wig by barcode for return
  const handleReturnBarcodeSearch = async () => {
    if (!returnBarcodeSearch.trim()) {
      toast.error('Please enter a barcode or order number');
      return;
    }

    try {
      setLoadingReturnSearch(true);
      const response = await fetch(
        `${API_URL}/api/rachels-room/search-return-wig/${returnBarcodeSearch.trim()}`
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Wig not found');
      }

      const wig = await response.json();

      // Check if wig was actually shipped out (has tracking number)
      if (!wig.trackingNumber || wig.trackingNumber.trim() === '') {
        toast.error('This wig was never shipped out (no tracking number found)');
        setReturnBarcodeSearch('');
        return;
      }

      // Check if already received
      if (wig.isShipmentReceived === 1 || wig.isShipmentReceived === true) {
        toast.error('This wig has already been marked as received');
        setReturnBarcodeSearch('');
        return;
      }

      // Check if already in return list
      if (returnItems.some(item => item.barcode === wig.barcode)) {
        toast.info('This wig is already in the return list');
        setReturnBarcodeSearch('');
        return;
      }

      // Add to return items
      setReturnItems(prev => [...prev, wig]);
      setReturnBarcodeSearch('');

    } catch (error) {
      console.error('Error searching return wig:', error);
      toast.error(error.message);
    } finally {
      setLoadingReturnSearch(false);
    }
  };

  // Remove item from return list
  const handleRemoveReturnItem = (barcode) => {
    setReturnItems(prev => prev.filter(item => item.barcode !== barcode));
  };

  // Create return shipment
  const handleCreateReturnShipment = async () => {
    // Validation
    if (returnForm.shippingToName === 'Select Location') {
      toast.error('Please select a shipping location');
      return;
    }
    if (!returnForm.trackingNumber.trim()) {
      toast.error('Tracking number is required');
      return;
    }
    if (returnItems.length === 0) {
      toast.error('No wigs added to return list');
      return;
    }

    try {
      setCreatingReturn(true);
      const response = await fetch(`${API_URL}/api/rachels-room/create-return-shipment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shippingToName: returnForm.shippingToName,
          courrierName: returnForm.courrierName,
          trackingNumber: returnForm.trackingNumber,
          specialInstructions: returnForm.specialInstructions,
          selectedBarcodes: returnItems.map(item => item.barcode),
          createdBy: currentUser?.username || currentUser || 'System'
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create return shipment');
      }

      const result = await response.json();
      toast.success(`${result.message} - ${result.wigsReturned} wigs received`);

      // Store the shipment ID for PDF printing
      setLastCreatedReturnId(result.returnShipmentId);

      // Reset form
      setReturnForm({
        shippingToName: 'Select Location',
        courrierName: 'Driver',
        trackingNumber: '',
        specialInstructions: ''
      });
      setReturnItems([]);

      // Show print view
      setShowReturnPrintView(true);

    } catch (error) {
      console.error('Error creating return shipment:', error);
      toast.error(error.message);
    } finally {
      setCreatingReturn(false);
    }
  };

  // Fetch wigs not in possession (shipped but not received back)
  const handleShowWigsNotInPossession = async () => {
    if (showWigsNotInPossession) {
      // Toggle off - just hide
      setShowWigsNotInPossession(false);
      return;
    }

    try {
      setLoadingWigsNotInPossession(true);
      const response = await fetch(`${API_URL}/api/rachels-room/wigs-not-in-possession`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch wigs');
      }

      const wigs = await response.json();
      setWigsNotInPossession(wigs);
      setShowWigsNotInPossession(true);

    } catch (error) {
      console.error('Error fetching wigs not in possession:', error);
      toast.error(error.message);
    } finally {
      setLoadingWigsNotInPossession(false);
    }
  };

  // Add a wig from "not in possession" list to return items
  const handleAddFromNotInPossession = (wig) => {
    // Check if wig has tracking number (was actually shipped)
    if (!wig.trackingNumber || wig.trackingNumber.trim() === '') {
      toast.error('This wig has no tracking number');
      return;
    }

    // Check if already in return list
    if (returnItems.some(item => item.barcode === wig.barcode)) {
      toast.info('This wig is already in the return list');
      return;
    }

    // Add to return items
    setReturnItems(prev => [...prev, {
      barcode: wig.barcode,
      orderNo: wig.orderNo,
      customerName: wig.customerName,
      wigType: wig.wigType,
      trackingNumber: wig.trackingNumber
    }]);
  };

  // Toggle checkbox for a wig
  const handleToggleShipping = (barcode) => {
    setSelectedForShipping(prev => {
      if (prev.includes(barcode)) {
        return prev.filter(b => b !== barcode);
      } else {
        return [...prev, barcode];
      }
    });
  };

  const handleWigInfoChange = (field, value) => {
    setWigInfo(prev => ({ ...prev, [field]: value }));

    // Handle "Other" for Wig Type
    if (field === 'wigType') {
      if (value === 'Other') {
        setShowWigTypeOther(true);
      } else {
        setShowWigTypeOther(false);
        setWigTypeOther('');
      }
    }

    // Handle "Other" for Top Size
    if (field === 'topSize') {
      if (value === 'Other') {
        setShowTopSizeOther(true);
      } else {
        setShowTopSizeOther(false);
        setTopSizeOther('');
      }
    }
  };

  const handleNewWigChange = (field, value) => {
    setNewWig(prev => ({ ...prev, [field]: value }));
  };

  const handleAddWig = async () => {
    // Validation
    if (!newWig.barcode) {
      alert('Barcode is required');
      return;
    }
    // if (!wigInfo.baseColor || !wigInfo.wigType || !wigInfo.wigLength) {
    //   alert('Please fill in all required wig characteristics (Base Color, Wig Type, Length, Net Size)');
    //   return;
    // }

    // const finalWigType = showWigTypeOther ? wigTypeOther : wigInfo.wigType;
    // if (!finalWigType) {
    //   alert('Please specify wig type');
    //   return;
    // }

    // const finalTopSize = showTopSizeOther ? topSizeOther : wigInfo.topSize;
    // if (!finalTopSize) {
    //   alert('Please specify top size');
    //   return;
    // }

    try {
      setCreating(true);

      // Use custom "Other" values if specified
      const finalWigType = showWigTypeOther ? wigTypeOther : wigInfo.wigType;
      const finalTopSize = showTopSizeOther ? topSizeOther : wigInfo.topSize;

      const payload = {
        barcode: newWig.barcode,
        topCode: newWig.topCode,
        wigWeight: newWig.wigWeight ? parseFloat(newWig.wigWeight) : null,
        comments: newWig.comments,
        baseColor: wigInfo.baseColor,
        wigType: finalWigType,
        wigLength: wigInfo.wigLength,
        netSize: wigInfo.netSize,
        hairType: wigInfo.hairType,
        netType: wigInfo.netType,
        laceType: wigInfo.laceType,
        topSize: finalTopSize,
        stockType: wigInfo.stockType,
        cutByStyle: wigInfo.stylist,
        createdBy: currentUser,
        orderPhone: "8450001256",
        orderStatus: "In-Process",
      };

      const response = await fetch(`${API_URL}/api/rachels-room/create-wig`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create wig');
      }

      const result = await response.json();
      toast.success(`Stock wig created successfully!\nOrder #: ${formatStockWigOrderNo(result.orderNo)}\nBarcode: ${result.barcode}`);

      // Reset form
      setNewWig({ barcode: '', topCode: '', wigWeight: '', comments: '' });

      // Reset to page 1 and refresh inventory
      setCurrentPage(1);
      await fetchInventory();

      // Auto-select the newly created wig and load its room tasks
      const createdWig = {
        barcode: result.barcode,
        orderNo: result.orderNo
      };
      setSelectedWig(createdWig);
      await fetchRoomTasks(result.orderNo);

    } catch (error) {
      toast.error(`Failed to create wig: ${error.message}`);
    } finally {
      setCreating(false);
    }
  };

  const handleSelectWig = async (wig) => {
    setSelectedWig(wig);
    await fetchRoomTasks(wig.orderNo);
  };

  const handleSearch = (value) => {
    setSearchBarcode(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // const handleDeleteWig = async (barcode) => {
  //   if (!window.confirm(`Delete stock wig ${barcode}?`)) return;

  //   try {
  //     const response = await fetch(`${API_URL}/api/rachels-room/inventory/${barcode}`, {
  //       method: 'DELETE'
  //     });

  //     if (!response.ok) throw new Error('Failed to delete wig');

  //     alert('Wig deleted successfully');

  //     // Clear selection if this was the selected wig
  //     if (selectedWig?.barcode === barcode) {
  //       setSelectedWig(null);
  //       setRoomTasks([]);
  //     }

  //     // Refresh inventory
  //     await fetchInventory();

  //   } catch (error) {
  //     console.error('Error deleting wig:', error);
  //     alert(`Failed to delete wig: ${error.message}`);
  //   }
  // };

  const handleUpdateTask = async (taskId, field, value) => {
    try {
      const response = await fetch(`${API_URL}/api/rachels-room/update-task`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId, field, value })
      });

      if (!response.ok) throw new Error('Failed to update task');

      // Refresh room tasks
      if (selectedWig) {
        await fetchRoomTasks(selectedWig.orderNo);
      }

    } catch (error) {
      console.error('Error updating task:', error);
      alert(`Failed to update task: ${error.message}`);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const response = await fetch(`${API_URL}/api/rachels-room/delete-task/${taskId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete task');
      }

      // Refresh room tasks
      if (selectedWig) {
        await fetchRoomTasks(selectedWig.orderNo);
      }

    } catch (error) {
      console.error('Error deleting task:', error);
      alert(`Failed to delete task: ${error.message}`);
    }
  };

  const handleAddLostRooms = async () => {
    if (!selectedWig) {
      toast.error('Please select a wig first');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/rachels-room/add-lost-rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderNo: selectedWig.orderNo })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add lost rooms');
      }

      const result = await response.json();

      if (result.addedCount === 0) {
        toast.info('No missing rooms to add - all rooms are already present');
      } else {
        toast.success(`Successfully added ${result.addedCount} missing room(s)`);
      }

      // Refresh room tasks
      await fetchRoomTasks(selectedWig.orderNo);

    } catch (error) {
      console.error('Error adding lost rooms:', error);
      toast.error(`Failed to add lost rooms: ${error.message}`);
    }
  };

  const handlePrintWorksheet = () => {
    if (!selectedWig) {
      toast.error('Please select a wig first');
      return;
    }
    setShowWorksheet(true);
  };

  const handleCloseWorksheet = () => {
    setShowWorksheet(false);
  };

  // Fetch available transfer rooms when a wig is selected
  useEffect(() => {
    if (selectedWig && roomTasks.length > 0) {
      fetchAvailableTransferRooms();
    } else {
      setAvailableTransferRooms([]);
      setSelectedTransferRoom('');
      setCurrentRoomInfo(null);
    }
  }, [selectedWig, roomTasks]);

  const fetchAvailableTransferRooms = async () => {
    try {
      const response = await fetch(`${API_URL}/api/orders/${selectedWig.orderNo}/available-rooms`);
      if (!response.ok) throw new Error('Failed to fetch transfer rooms');

      const data = await response.json();

      // Use the authoritative current room from the API (from V_Get_Current_Room view)
      const currentRoomNo = data.currentRoom;

      // Find the current room task from roomTasks using the current room number
      const currentTask = roomTasks.find(task =>
        String(task.RoomNo) === String(currentRoomNo)
      );

      // IMPORTANT: Always trust the backend's current room determination
      // Only use fallback if the backend didn't provide a current room
      if (currentTask) {
        setCurrentRoomInfo(currentTask);
      } else if (currentRoomNo) {
        // Backend says there's a current room but we can't find it in our tasks
        console.warn(`⚠️ Backend says current room is ${currentRoomNo} but task not found in room tasks`);
        // Still set it with basic info so transfers work
        setCurrentRoomInfo({
          RoomNo: currentRoomNo,
          RoomTask: `Room ${currentRoomNo}`,
          TaskStatus: 'Unknown'
        });
      } else {
        // No current room from backend, fall back to first incomplete task
        const fallbackTask = roomTasks.find(task =>
          task.TaskStatus !== 'Task Completed' &&
          !task.TaskStatus?.toLowerCase().includes('reject')
        ) || roomTasks[0];
        setCurrentRoomInfo(fallbackTask);
      }

      // Filter available rooms - exclude completed tasks and current task
      const availableRooms = data.availableRooms.filter(room => {
        // Exclude the current task
        if (room.taskId === currentTask?.ID) return false;

        // Exclude completed tasks
        if (room.currentTaskStatus === 'Task Completed') return false;

        // Exclude rejected tasks
        if (room.currentTaskStatus?.toLowerCase().includes('reject')) return false;

        return true;
      });

      setAvailableTransferRooms(availableRooms);
      setSelectedTransferRoom(''); // Reset selection

      console.log('Current room from API:', currentRoomNo);
      console.log('Current task found:', currentTask);

    } catch (error) {
      console.error('Error fetching transfer rooms:', error);
      toast.error('Failed to load transfer rooms');
    }
  };

  const handleTransferWig = async () => {
    if (!selectedTransferRoom) {
      toast.error('Please select a destination room');
      return;
    }

    if (!currentRoomInfo) {
      toast.error('No current room found');
      return;
    }

    try {
      setTransferring(true);

      // Parse the selected room-task combination
      const [targetRoomNo, targetTaskId] = selectedTransferRoom.split('-');

      // Format room strings as expected by the backend (room-taskId format)
      const fromRoomStr = currentRoomInfo.ID ?
        `${currentRoomInfo.RoomNo}-${currentRoomInfo.ID}` :
        String(currentRoomInfo.RoomNo);

      const toRoomStr = targetTaskId ?
        `${targetRoomNo}-${targetTaskId}` :
        targetRoomNo;

      const transferPayload = {
        orderNo: parseInt(selectedWig.orderNo),
        fromRoom: fromRoomStr,      // String format: "5001-123"
        toRoom: toRoomStr,          // String format: "1001-456"
        transferredBy: currentUser,

        // Keep scan data for compatibility
        scanOutData: {
          roomNo: parseInt(currentRoomInfo.RoomNo),
          orderNo: parseInt(selectedWig.orderNo)
        },

        scanInData: {
          roomNo: parseInt(targetRoomNo),
          orderNo: parseInt(selectedWig.orderNo),
          taskId: targetTaskId ? parseInt(targetTaskId) : null
        }
      };

      console.log('Transfer payload:', transferPayload);

      const response = await fetch(`${API_URL}/api/orders/${selectedWig.orderNo}/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(transferPayload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Transfer failed');
      }

      const result = await response.json();

      toast.success(
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            ✅ Transfer Completed Successfully!
          </div>
          <div style={{ fontSize: '0.9em', lineHeight: '1.4' }}>
            📤 Scanned OUT of Room {currentRoomInfo.RoomNo}<br/>
            📥 Scanned IN to Room {targetRoomNo}<br/>
            👤 Transferred by: {currentUser}<br/>
            🕐 Time: {new Date().toLocaleString()}
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );

      // Refresh room tasks to show updated status
      await fetchRoomTasks(selectedWig.orderNo);

    } catch (error) {
      console.error('Error transferring wig:', error);
      toast.error(`Transfer failed: ${error.message}`);
    } finally {
      setTransferring(false);
    }
  };

  return (
    <div className="rachels-room-container">
      <div className="PageShadowTable" style={{ width: '99%', padding: '20px' }}>

        {/* Header */}
        <div className="rachels-room-header">
          <h1>Rachel's Room - Stock Wig Inventory</h1>
        </div>

        {/* Wig Info Section */}
        <div className="PageShadowTable" style={{ padding: '15px', marginBottom: '20px' }}>
          <h2>Wig Info</h2>
          <div className="wig-info-grid">
            <div className="form-group">
              <label>Stylist</label>
            <select
                value={wigInfo.stylist}
                onChange={(e) => handleWigInfoChange('stylist', e.target.value)}
              >
                <option value="">Select...</option>
                {WIG_OPTIONS.cutStyles.map(style => (
                  <option key={style} value={style}>{style}</option>
                ))}
              </select>
           
            </div>
            <div className="form-group">
              <label>Base Color</label>
              <select
                value={wigInfo.baseColor}
                onChange={(e) => handleWigInfoChange('baseColor', e.target.value)}
              >
                <option value="">Select...</option>
                {getColorOptions(wigInfo.wigType).map(color => (
                  <option key={color} value={color}>{color}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Wig Type</label>
              <select
                value={wigInfo.wigType}
                onChange={(e) => handleWigInfoChange('wigType', e.target.value)}
              >
                <option value="">Select...</option>
                {WIG_OPTIONS.wigTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
                <option value="Other">Other</option>
              </select>
              {showWigTypeOther && (
                <input
                  type="text"
                  placeholder="Specify wig type..."
                  value={wigTypeOther}
                  onChange={(e) => setWigTypeOther(e.target.value)}
                  style={{ marginTop: '5px' }}
                />
              )}
            </div>

            <div className="form-group">
              <label>Wig Length</label>
              <select
                value={wigInfo.wigLength}
                onChange={(e) => handleWigInfoChange('wigLength', e.target.value)}
              >
                <option value="">Select...</option>
                {WIG_OPTIONS.hairLengths.map(length => (
                  <option key={length} value={length}>{length}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Net Size</label>
              <select
                value={wigInfo.netSize}
                onChange={(e) => handleWigInfoChange('netSize', e.target.value)}
              >
                <option value="">Select...</option>
                {WIG_OPTIONS.netSizes.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Hair Type</label>
              <select
                value={wigInfo.hairType}
                onChange={(e) => handleWigInfoChange('hairType', e.target.value)}
              >
                <option value="">Select...</option>
                {WIG_OPTIONS.hairTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Net Type</label>
              <select
                value={wigInfo.netType}
                onChange={(e) => handleWigInfoChange('netType', e.target.value)}
              >
                <option value="">Select...</option>
                {WIG_OPTIONS.netTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Lace Type</label>
              <select
                value={wigInfo.laceType}
                onChange={(e) => handleWigInfoChange('laceType', e.target.value)}
              >
                <option value="">Select...</option>
                {WIG_OPTIONS.laceTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Top Size</label>
              <select
                value={wigInfo.topSize}
                onChange={(e) => handleWigInfoChange('topSize', e.target.value)}
              >
                <option value="">Select...</option>
                {WIG_OPTIONS.topSizes.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
                <option value="Other">Other</option>
              </select>
              {showTopSizeOther && (
                <input
                  type="text"
                  placeholder="Specify top size..."
                  value={topSizeOther}
                  onChange={(e) => setTopSizeOther(e.target.value)}
                  style={{ marginTop: '5px' }}
                />
              )}
            </div>

            <div className="form-group">
              <label>Stock Type</label>
              <select
                value={wigInfo.stockType}
                onChange={(e) => handleWigInfoChange('stockType', e.target.value)}
              >
                <option value="Stock">Stock</option>
                <option value="Custom">Custom</option>
                <option value="Wholesaler">Wholesaler</option>
              </select>
            </div>
          </div>
          <div className="new-wig-form">
            <div className="form-group">
              <label>Barcode <span style={{ color: 'red' }}>*</span></label>
              <input
                type="text"
                value={newWig.barcode}
                onChange={(e) => handleNewWigChange('barcode', e.target.value)}
                placeholder="Enter barcode..."
              />
            </div>
            
            <div className="form-group">
              <label>Wig Weight (g)</label>
              <input
                type="number"
                value={newWig.wigWeight}
                onChange={(e) => handleNewWigChange('wigWeight', e.target.value)}
                onWheel={(e) => e.target.blur()}
                placeholder="Enter weight..."
                min="0"
                step="0.1"
              />
            </div>

            <div className="form-group">
              <label>Comments</label>
              <textarea
                value={newWig.comments}
                onChange={(e) => handleNewWigChange('comments', e.target.value)}
                placeholder="Enter comments..."
                rows="1"
              />
            </div>

            <div className="form-group" style={{ gridColumn: 'span 3' }}>
              <button
                onClick={handleAddWig}
                disabled={creating}
                className="add-wig-button"
              >
                {creating ? 'Creating...' : 'Add Wig'}
              </button>
            </div>
          </div>
        </div>

   

                {/* Room Tasks Grid */}
        {selectedWig && (
          <div className="PageShadowTable" style={{ padding: '15px'}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
              <h2>Room Tasks for {selectedWig.barcode} (Order: {formatStockWigOrderNo(selectedWig.orderNo)})</h2>

              {/* Transfer Controls */}
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                {currentRoomInfo && (
                  <>
                    <span style={{ fontWeight: 'bold', color: '#1C5E55' }}>
                      Current Room: {currentRoomInfo.RoomNo}
                    </span>
                    <span style={{ color: '#666' }}>|</span>
                    <select
                      value={selectedTransferRoom}
                      onChange={(e) => setSelectedTransferRoom(e.target.value)}
                      className="btn-small"
                      style={{ padding: '8px 12px', fontSize: '14px', minWidth: '150px' }}
                      disabled={availableTransferRooms.length === 0}
                    >
                      <option value="">Select Destination...</option>
                      {availableTransferRooms.map((room) => (
                        <option key={`${room.roomNo}-${room.taskId}`} value={`${room.roomNo}-${room.taskId}`}>
                          Room {room.roomNo} - {room.roomDescription}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleTransferWig}
                      disabled={!selectedTransferRoom || transferring}
                      className="btn-small btn-select"
                      style={{
                        padding: '8px 16px',
                        fontSize: '14px',
                        backgroundColor: selectedTransferRoom ? '#FF9800' : '#ccc',
                        cursor: selectedTransferRoom ? 'pointer' : 'not-allowed'
                      }}
                    >
                      {transferring ? 'Transferring...' : 'Transfer Wig'}
                    </button>
                    <span style={{ color: '#666' }}>|</span>
                  </>
                )}
                <button
                  onClick={handlePrintWorksheet}
                  className="btn-small btn-select"
                  style={{ padding: '8px 16px', fontSize: '14px', backgroundColor: '#007bff' }}
                >
                  Print Worksheet
                </button>
                <button
                  onClick={handleAddLostRooms}
                  title={'Restores standard rooms that were deleted from this wig by mistake.\n' +
                    'Adds back any of the rooms a new wig starts with that are missing, as "Not Started": ' +
                    '51001, 52001, 53001, 54001, 1001, 1002, 1003, 3001, 10001.\n' +
                    'Rooms already on the wig are not changed.'}
                  className="btn-small btn-select"
                  style={{ padding: '8px 16px', fontSize: '14px', backgroundColor: '#4CAF50' }}
                >
                  Add Lost Rooms
                </button>
              </div>
            </div>
            {roomTasks.length === 0 ? (
              <p>No room tasks found</p>
            ) : (
              <div className="room-tasks-table-container">
                <table className="room-tasks-table">
                  <thead>
                    <tr>
                      <th style={{ width: '5%' }}>Actions</th>
                      <th style={{ width: '10%' }}>Room No</th>
                      {/* <th>Room Name</th> */}
                      <th style={{ width: '20%' }}>Task</th>
                      {/* <th>Assigned Worker</th> */}
                      {/* <th>Due Date</th> */}
                      <th style={{ width: '30%' }}>Task Notes</th>
                      <th style={{ width: '30%' }}>QC Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                       {roomTasks.map((task) => (
                         <tr key={task.ID}>
                     <td>
                          <button
                            onClick={() => handleDeleteTask(task.ID)}
                            className="btn-small btn-delete"
                          >
                            Delete
                          </button>
                        </td>
                        <td>
                          <EditableCell
                            value={task.RoomNo}
                            onSave={(value) => handleUpdateTask(task.ID, 'RoomNo', value)}
                            placeholder="Enter room no..."
                          />
                          {/* {task.RoomNo} */}
                          </td>
                        {/* <td>{task.RoomDescription}</td> */}
                        <td>
                          <EditableCell
                            value={task.RoomTask}
                            onSave={(value) => handleUpdateTask(task.ID, 'RoomTask', value)}
                            placeholder="Enter task..."
                          />
                        </td>
                        {/* <td>{task.RoomTask}</td> */}
                        {/* <td>
                          <EditableCell
                            value={task.AssignedWorker}
                            onSave={(value) => handleUpdateTask(task.ID, 'AssignedWorker', value)}
                            placeholder="Assign..."
                          />
                        </td> */}
                        {/* <td>
                          <EditableCell
                            type="datetime-local"
                            value={task.TaskDueDate}
                            onSave={(value) => handleUpdateTask(task.ID, 'TaskDueDate', value)}
                            placeholder="Set date..."
                          />
                        </td> */}
                        <td>
                          <EditableCell
                            value={task.TaskNotes}
                            onSave={(value) => handleUpdateTask(task.ID, 'TaskNotes', value)}
                            placeholder="Add notes..."
                            multiline={true}
                          />
                        </td>
                        <td>
                          <EditableCell
                            value={task.QC_Notes}
                            onSave={(value) => handleUpdateTask(task.ID, 'QC_Notes', value)}
                            placeholder="Add QC notes..."
                            multiline={true}
                          />
                        </td>
                       
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

    
        {/* Side-by-Side Grids Container */}
        <div className="side-by-side-grids">

          {/* Rachel's Inventory Grid */}
          <div className="PageShadowTable" style={{ padding: '15px' }}>
              <h2>Rachel's Inventory</h2>

            {/* Search Bar */}
            <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search by barcode..."
                value={searchBarcode}
                onChange={(e) => handleSearch(e.target.value)}
                style={{
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  width: '300px'
                }}
              />
              {searchBarcode && (
                <button
                  onClick={() => handleSearch('')}
                  className="btn-small"
                  style={{ padding: '8px 12px' }}
                >
                  Clear Search
                </button>
              )}
              <span style={{ fontSize: '14px', color: '#666' }}>
                {searchBarcode ? `Found ${totalRecords} result(s)` : `Total: ${totalRecords} wig(s)`}
              </span>
            </div>

            {loading ? (
              <p>Loading inventory...</p>
            ) : inventory.length === 0 ? (
              <p>{searchBarcode ? 'No wigs found matching your search.' : 'No stock wigs found. Create one above!'}</p>
            ) : (
              <div className="rachels-inv-grid-container">
                <table className="rachels-inv-grid">
                  <thead>
                    <tr>
                      <th>Actions</th>
                      <th>BarCode</th>
                      <th>Order No</th>
                      <th>Weight (g)</th>
                      <th>
                        <table style={{ width: '100%', border: 'none', margin: 0 }}>
                          <tbody>
                            <tr>
                              {/* <td style={{ border: 'none', padding: '2px' }}>Select All</td> */}
                              <td colSpan="2" style={{ textAlign: 'center', border: 'none', padding: '2px' }}>Shipping</td>
                              <td style={{ border: 'none', padding: '2px' }}>
                                <input
                                  type="checkbox"
                                  checked={inventory.length > 0 && inventory.every(w => selectedForShipping.includes(w.barcode))}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      // Add all current page items to selection
                                      const currentPageBarcodes = inventory.map(w => w.barcode);
                                      setSelectedForShipping(prev => {
                                        const newSelection = [...prev];
                                        currentPageBarcodes.forEach(barcode => {
                                          if (!newSelection.includes(barcode)) {
                                            newSelection.push(barcode);
                                          }
                                        });
                                        return newSelection;
                                      });
                                    } else {
                                      // Remove only current page items from selection
                                      const currentPageBarcodes = inventory.map(w => w.barcode);
                                      setSelectedForShipping(prev => prev.filter(b => !currentPageBarcodes.includes(b)));
                                    }
                                  }}
                                />
                              </td>
                            </tr>
                        
                          
                                <button
                                  onClick={() => setSelectedForShipping([])}
                                  className="btn-small"
                                  style={{ fontSize: '11px', padding: '3px 8px' }}
                                >
                                  Clear All
                                </button>
                            
                        
                          </tbody>
                        </table>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventory.map((wig) => (
                      <tr
                        key={wig.barcode}
                        className={selectedWig?.barcode === wig.barcode ? 'selected-row' : ''}
                      >
                        <td>
                          <button
                            onClick={() => handleSelectWig(wig)}
                            className="btn-small btn-select"
                          >
                            Select
                          </button>
                          {/* <button
                            onClick={() => handleDeleteWig(wig.barcode)}
                            className="btn-small btn-delete"
                          >
                            Delete
                          </button> */}
                        </td>
                        <td>{wig.barcode}</td>
                        <td>{formatStockWigOrderNo(wig.orderNo)}</td>
                        {/* <td>{wig.wigType}</td> */}
                        {/* <td>{wig.netSize}</td> */}
                        {/* <td>{wig.baseColor}</td> */}
                        {/* <td>{wig.wigLength}</td> */}
                        {/* <td>{wig.hairType}</td> */}
                        {/* <td>{wig.invStatus}</td> */}
                        <td>{wig.wigWeight}</td>
                        <td
                          style={{
                            textAlign: 'center',
                            cursor: 'pointer',
                            padding: '8px',
                            userSelect: 'none'
                          }}
                          onClick={() => handleToggleShipping(wig.barcode)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedForShipping.includes(wig.barcode)}
                            onChange={() => {}} // Handled by td onClick
                            style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                          />
                        </td>
                        {/* <td>{new Date(wig.dateCreated).toLocaleDateString()}</td> */}
                      </tr>
                    ))}
                  </tbody>
                  
        
                </table>
              {/* Pagination Controls */}     
            <div style={{display: 'flex', gap: '10px', alignItems: 'center', marginTop: '15px', justifyContent: 'center'}}>
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="btn-small btn-select"
                    style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
                  >
                    First
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="btn-small btn-select"
                    style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="btn-small btn-select"
                    style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="btn-small btn-select"
                    style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
                  >
                    Last
                  </button>
                </div>
              </div>
              
            )}
                  {/* New Shipping Order Form */}
        <div className="PageShadowTable" style={{ padding: '20px', marginBottom: '20px' }}>
          <h2 style={{ marginBottom: '15px' }}>Enter New Shipment Details</h2>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Location: <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                value={shippingForm.shippingToName}
                onChange={(e) => setShippingForm({ ...shippingForm, shippingToName: e.target.value })}
                style={{
                  padding: '8px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  minWidth: '200px'
                }}
              >
                <option value="Select Location">Select Location</option>
<option value="China">China</option>
<option value="China - Old Fac (50001)">China - Old Fac (50001)</option>
<option value="China - BT Stock (51001)">China - BT Stock (51001)</option>
<option value="China - New Line (52001)">China - New Line (52001)</option>
<option value="China - BT Cust (53001)">China - BT Cust (53001)</option>
<option value="China - ABC (54001)">China - ABC (54001)</option>
<option value="China - Debby (55001)">China - Debby (55001)</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Ship By:
              </label>
              <select
                value={shippingForm.courrierName}
                onChange={(e) => setShippingForm({ ...shippingForm, courrierName: e.target.value })}
                style={{
                  padding: '8px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  minWidth: '120px'
                }}
              >
                <option value="Driver">Driver</option>
                <option value="FedEx">FedEx</option>
                <option value="USPS">USPS</option>
                <option value="DHL">DHL</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Tracking No: <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                value={shippingForm.trackingNumber}
                onChange={(e) => setShippingForm({ ...shippingForm, trackingNumber: e.target.value })}
                placeholder="Enter tracking number"
                style={{
                  padding: '8px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  minWidth: '200px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Special Instructions:
              </label>
              <input
                type="text"
                value={shippingForm.specialInstructions}
                onChange={(e) => setShippingForm({ ...shippingForm, specialInstructions: e.target.value })}
                placeholder="Enter special instructions"
                style={{
                  padding: '8px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  minWidth: '250px'
                }}
              />
            </div>

            <div>
              <button
                onClick={handleCreateShipment}
                disabled={creatingShipment}
                className="btn-small btn-select"
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  backgroundColor: '#27ae60',
                  opacity: creatingShipment ? 0.6 : 1
                }}
              >
                {creatingShipment ? 'Creating...' : 'Create Shipping Order'}
              </button>
              
            </div>           
          </div>
               {selectedForShipping.length > 0 && (
              <div style={{ marginLeft: 'auto' }}>
                <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#1976d2' }}>
                  {selectedForShipping.length} total wig(s) selected
                  {customerOrders.length > 0 && (
                    <span style={{ fontSize: '12px', marginLeft: '8px', color: '#7f8c8d' }}>
                      ({selectedForShipping.length - customerOrders.length} stock + {customerOrders.length} customer)
                    </span>
                  )}
                </span>
              </div>
            )}
        </div>
        {/* Customer Order Search */}
        <div className="PageShadowTable" style={{ padding: '20px', marginBottom: '20px' }}>
          <h2 style={{ marginBottom: '15px' }}>Add Customer Order to Shipment</h2>

          <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', marginBottom: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Order Number:
              </label>
              <input
                type="number"
                value={customerOrderSearch}
                onChange={(e) => setCustomerOrderSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCustomerOrderSearch();
                  }
                }}
                placeholder="Enter order number..."
                style={{
                  padding: '8px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  minWidth: '200px'
                }}
              />
            </div>

            <button
              onClick={handleCustomerOrderSearch}
              disabled={loadingCustomerOrders}
              className="btn-small btn-select"
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 'bold',
                backgroundColor: '#3498db',
                opacity: loadingCustomerOrders ? 0.6 : 1
              }}
            >
              {loadingCustomerOrders ? 'Searching...' : 'Add Customer Order'}
            </button>
          </div>

          {/* Display selected customer orders */}
          {customerOrders.length > 0 && (
            <div>
              <h3 style={{ fontSize: '16px', marginBottom: '10px', color: 'var(--text-primary)' }}>
                Selected Customer Orders ({customerOrders.length}):
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {customerOrders.map((order) => (
                  <div
                    key={order.barcode}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#e3f2fd',
                      borderRadius: '4px',
                      border: '1px solid #2196f3',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#1976d2' }}>
                      Order #{order.orderNo}
                    </span>
                    <span style={{ fontSize: '13px', color: '#34495e' }}>
                      {order.customerName} - {order.wigType}
                    </span>
                    <button
                      onClick={() => handleRemoveCustomerOrder(order.barcode)}
                      style={{
                        backgroundColor: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        padding: '3px 8px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
          </div>

          {/* Shipping Grid */}
          <div className="PageShadowTable" style={{ padding: '15px' }}>
            <h2>Shipments</h2>

            {/* Selected Shipment Indicator */}
            {selectedShipment && (
              <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#e3f2fd33', borderRadius: '4px', border: '1px solid #2196f3' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <strong>Selected Shipment ID:</strong>&nbsp;
                    <span style={{ color: '#1976d2', fontSize: '16px', fontWeight: 'bold' }}>
                      #{selectedShipment.shipmentDetails.shipmentId}
                    </span>
                    &nbsp;-&nbsp;
                    <span>{selectedShipment.shipmentDetails.ShippingToName}</span>
                    {selectedShipment.shipmentDetails.ShipOutDate && (
                      <span style={{
                        marginLeft: '10px',
                        color: new Date(selectedShipment.shipmentDetails.ShipOutDate).getFullYear() < 1901 ? '#3498db' : '#27ae60',
                        fontWeight: 'bold'
                      }}>
                        {new Date(selectedShipment.shipmentDetails.ShipOutDate).getFullYear() < 1901
                          ? '(Received)'
                          : `(Sent: ${new Date(selectedShipment.shipmentDetails.ShipOutDate).toLocaleDateString()})`}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={handlePrintShipping}
                      className="btn-small btn-select"
                      style={{ backgroundColor: '#007bff' }}
                    >
                      Print
                    </button>
                    <button
                      onClick={handleExportToSheets}
                      className="btn-small btn-select"
                      disabled={exporting}
                      style={{ backgroundColor: '#28a745' }}
                    >
                      {exporting ? 'Exporting...' : 'Export to Sheets'}
                    </button>
                    {!selectedShipment.shipmentDetails.ShipOutDate && (
                      <input
                        type="date"
                        value={sentDate}
                        onChange={(e) => setSentDate(e.target.value)}
                        style={{
                          padding: '6px 8px',
                          fontSize: '13px',
                          borderRadius: '4px',
                          border: '1px solid #ccc'
                        }}
                      />
                    )}
                    <button
                      onClick={handleMarkSent}
                      className="btn-small btn-select"
                      disabled={!!selectedShipment.shipmentDetails.ShipOutDate}
                      style={{
                        backgroundColor: selectedShipment.shipmentDetails.ShipOutDate ? '#95a5a6' : '#27ae60',
                        opacity: selectedShipment.shipmentDetails.ShipOutDate ? 0.6 : 1,
                        cursor: selectedShipment.shipmentDetails.ShipOutDate ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Ship Out
                    </button>
                  </div>
                </div>
              </div>
            )}

            {loadingShipments ? (
              <p>Loading shipments...</p>
            ) : shipments.length === 0 ? (
              <p>No shipments found</p>
            ) : (
              <div className="shipments-grid-container">
                <table className="shipments-grid">
                  <thead>
                    <tr>
                      <th>Actions</th>
                      <th>ID</th>
                      <th>To</th>
                      <th>Entry Date</th>
                      <th>Ship Out Date</th>
                      <th>Courier</th>
                      <th>Tracking</th>
                      <th>Wigs</th>
                      <th>Weight (g)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shipments.map((shipment) => (
                      <tr
                        key={shipment.shipmentId}
                        className={selectedShipment?.shipmentDetails?.shipmentId === shipment.shipmentId ? 'selected-row' : ''}
                      >
                        <td>
                          <button
                            onClick={() => handleSelectShipment(shipment)}
                            className="btn-small btn-select"
                          >
                            Select
                          </button>
                        </td>
                        <td>{shipment.shipmentId}</td>
                        <td>{shipment.ShippingToName}</td>
                        <td>
                          <EditableCell
                            type="date"
                            value={shipment.EntryDate ? new Date(shipment.EntryDate).toISOString().split('T')[0] : ''}
                            onSave={(value) => handleUpdateShipmentEntryDate(shipment.shipmentId, value)}
                            placeholder="Set date..."
                          />
                        </td>
                        <td style={{
                          color: shipment.ShipOutDate
                            ? (new Date(shipment.ShipOutDate).getFullYear() < 1901 ? '#3498db' : '#27ae60')
                            : '#e74c3c',
                          fontWeight: shipment.ShipOutDate ? 'normal' : 'bold'
                        }}>
                          {shipment.ShipOutDate
                            ? (new Date(shipment.ShipOutDate).getFullYear() < 1901
                                ? 'Received'
                                : new Date(shipment.ShipOutDate).toLocaleDateString())
                            : 'Not Sent'}
                        </td>

                        <td>{shipment.CourrierName || '-'}</td>
                        
                        <td>
                          <EditableCell
                            value={shipment.TrackingNumber || ''}
                            onSave={(value) => handleUpdateShipmentTracking(shipment.shipmentId, value)}
                            placeholder="Enter tracking..."
                          />
                        </td>
                        <td>{shipment.wigCount}</td>
                        <td>{shipment.totalWeight ? parseFloat(shipment.totalWeight).toFixed(3) : '0'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination Controls */}
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '15px', justifyContent: 'center' }}>
                  <button
                    onClick={() => setShipmentPage(1)}
                    disabled={shipmentPage === 1}
                    className="btn-small btn-select"
                    style={{ opacity: shipmentPage === 1 ? 0.5 : 1 }}
                  >
                    First
                  </button>
                  <button
                    onClick={() => setShipmentPage(prev => Math.max(1, prev - 1))}
                    disabled={shipmentPage === 1}
                    className="btn-small btn-select"
                    style={{ opacity: shipmentPage === 1 ? 0.5 : 1 }}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
                    Page {shipmentPage} of {shipmentTotalPages} ({shipmentTotalRecords} total)
                  </span>
                  <button
                    onClick={() => setShipmentPage(prev => Math.min(shipmentTotalPages, prev + 1))}
                    disabled={shipmentPage === shipmentTotalPages}
                    className="btn-small btn-select"
                    style={{ opacity: shipmentPage === shipmentTotalPages ? 0.5 : 1 }}
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setShipmentPage(shipmentTotalPages)}
                    disabled={shipmentPage === shipmentTotalPages}
                    className="btn-small btn-select"
                    style={{ opacity: shipmentPage === shipmentTotalPages ? 0.5 : 1 }}
                  >
                    Last
                  </button>

                  
                </div>
    {/* Selected Shipment Details */}
                {selectedShipment && shipmentWigs && shipmentWigs.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <h3>Wigs in Shipment ({shipmentWigs.length})</h3>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <table className="room-tasks-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Barcode</th>
                            <th>Wig Type</th>
                            <th>Customer</th>
                            <th>Instructions</th>
                            <th>Weight (g)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {shipmentWigs.map((wig) => (
                            <tr key={wig.Number}>
                              <td>{wig.Number}</td>
                              <td>
                                <EditableCell
                                  value={wig.Wig_Bar_Code}
                                  onSave={(value) => handleUpdateShipmentWig(wig.Wig_Bar_Code, 'barcode', value)}
                                  placeholder="Barcode..."
                                />
                              </td>
                              <td>
                                <EditableCell
                                  value={wig.OrderWigType}
                                  onSave={(value) => handleUpdateShipmentWig(wig.Wig_Bar_Code, 'wigType', value)}
                                  placeholder="Wig type..."
                                />
                              </td>
                              <td>
                                <EditableCell
                                  value={wig.OrderCustomerName}
                                  onSave={(value) => handleUpdateShipmentWig(wig.Wig_Bar_Code, 'customerName', value)}
                                  placeholder="Customer..."
                                />
                              </td>
                              <td>
                                <EditableCell
                                  value={wig.ShippingInstructions || ''}
                                  onSave={(value) => handleUpdateShipmentWig(wig.Wig_Bar_Code, 'instructions', value)}
                                  placeholder="Add instructions..."
                                  multiline={true}
                                />
                              </td>
                              <td>
                                <EditableCell
                                  value={wig.WigWeight ? parseFloat(wig.WigWeight).toFixed(3) : '0'}
                                  onSave={(value) => handleUpdateWigWeight(wig.Wig_Bar_Code, value)}
                                  placeholder="Weight..."
                                  type="text"
                                />
                              </td>
                              
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>
                            <td colSpan="5" style={{ textAlign: 'right' }}>Total Weight:</td>
                            <td>{selectedShipment.totalWeight} g</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
                
        {/* Track Return Shipping Order Form */}
        <div className="PageShadowTable" style={{ padding: '20px', marginBottom: '20px' }}>
          <h2 style={{ marginBottom: '15px', color: '#27ae60' }}>Track Shipment Returns</h2>

          {/* Barcode Search Row */}
          <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', marginBottom: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Barcode / Order #:
              </label>
              <input
                type="text"
                value={returnBarcodeSearch}
                onChange={(e) => setReturnBarcodeSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleReturnBarcodeSearch();
                  }
                }}
                placeholder="Scan barcode or enter order #..."
                style={{
                  padding: '8px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  minWidth: '220px'
                }}
              />
            </div>

            <button
              onClick={handleReturnBarcodeSearch}
              disabled={loadingReturnSearch}
              className="btn-small btn-select"
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 'bold',
                backgroundColor: '#3498db',
                opacity: loadingReturnSearch ? 0.6 : 1
              }}
            >
              {loadingReturnSearch ? 'Searching...' : 'Add Item'}
            </button>
              <button
              onClick={handleShowWigsNotInPossession}
              disabled={loadingWigsNotInPossession}
              className="btn-small btn-select"
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 'bold',
                backgroundColor: showWigsNotInPossession ? '#95a5a6' : '#9b59b6',
                opacity: loadingWigsNotInPossession ? 0.6 : 1
              }}
            >
              {loadingWigsNotInPossession
                ? 'Loading...'
                : showWigsNotInPossession
                  ? 'Hide Wigs Not In Possession'
                  : 'Show Wigs Not In Possession'}
            </button>
          </div>

          {/* Form Fields Row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Location: <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                value={returnForm.shippingToName}
                onChange={(e) => setReturnForm({ ...returnForm, shippingToName: e.target.value })}
                style={{
                  padding: '8px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  minWidth: '200px'
                }}
              >
                <option value="Select Location">Select Location</option>
                <option value="China">China</option>
                <option value="China - Old Fac (50001)">China - Old Fac (50001)</option>
                <option value="China - BT Stock (51001)">China - BT Stock (51001)</option>
                <option value="China - New Line (52001)">China - New Line (52001)</option>
                <option value="China - BT Cust (53001)">China - BT Cust (53001)</option>
                <option value="China - ABC (54001)">China - ABC (54001)</option>
                <option value="China - Debby (55001)">China - Debby (55001)</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Ship By:
              </label>
              <select
                value={returnForm.courrierName}
                onChange={(e) => setReturnForm({ ...returnForm, courrierName: e.target.value })}
                style={{
                  padding: '8px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  minWidth: '120px'
                }}
              >
                <option value="Driver">Driver</option>
                <option value="FedEx">FedEx</option>
                <option value="USPS">USPS</option>
                <option value="DHL">DHL</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Tracking No: <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                value={returnForm.trackingNumber}
                onChange={(e) => setReturnForm({ ...returnForm, trackingNumber: e.target.value })}
                placeholder="Enter tracking number"
                style={{
                  padding: '8px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  minWidth: '200px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Notes
              </label>
              <input
                type="text"
                value={returnForm.specialInstructions}
                onChange={(e) => setReturnForm({ ...returnForm, specialInstructions: e.target.value })}
                placeholder="Additional Notes..."
                style={{
                  padding: '8px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  minWidth: '250px'
                }}
              />
            </div>

            <button
              onClick={handleCreateReturnShipment}
              disabled={creatingReturn || returnItems.length === 0}
              className="btn-small btn-select"
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 'bold',
                backgroundColor: '#27ae60',
                opacity: (creatingReturn || returnItems.length === 0) ? 0.6 : 1
              }}
            >
              {creatingReturn ? 'Recording...' : 'Record Return'}
            </button>
          </div>

          {/* Return Items Count */}
          {returnItems.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#27ae60' }}>
                {returnItems.length} wig(s) ready for return
              </span>
            </div>
          )}

          {/* Return Items Table */}
          {returnItems.length > 0 && (
            <div style={{ marginTop: '15px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#27ae60', color: 'white' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Barcode</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Order #</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Customer</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Wig Type</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Original Tracking</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {returnItems.map((item, index) => (
                    <tr key={item.barcode} style={{ backgroundColor: index % 2 === 0 ? 'var(--dark-bg)' : 'var(--dark-elevated' }}>
                      <td style={{ padding: '6px', borderBottom: '1px solid #ddd' }}>{item.barcode}</td>
                      <td style={{ padding: '6px', borderBottom: '1px solid #ddd' }}>{item.orderNo}</td>
                      <td style={{ padding: '6px', borderBottom: '1px solid #ddd' }}>{item.customerName}</td>
                      <td style={{ padding: '6px', borderBottom: '1px solid #ddd' }}>{item.wigType || '-'}</td>
                      <td style={{ padding: '6px', borderBottom: '1px solid #ddd' }}>{item.trackingNumber || '-'}</td>
                      <td style={{ padding: '6px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>
                        <button
                          onClick={() => handleRemoveReturnItem(item.barcode)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#e74c3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

         

          {/* Wigs Not In Possession Table */}
          {showWigsNotInPossession && wigsNotInPossession.length > 0 && (
            <div style={{ marginTop: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '10px' }}>
                <h4 style={{ margin: 0, color: '#9b59b6' }}>
                  Wigs Shipped Out But Not Received ({wigsNotInPossession.length})
                </h4>
                <input
                  type="text"
                  value={wigsNotInPossessionSearch}
                  onChange={(e) => setWigsNotInPossessionSearch(e.target.value)}
                  placeholder="Search barcode, order #, customer..."
                  style={{
                    padding: '6px 10px',
                    fontSize: '13px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    minWidth: '250px'
                  }}
                />
              </div>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '16px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#9b59b6', color: 'var(--text-primary)', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Barcode</th>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Order #</th>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Customer</th>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Shipped To</th>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Ship Date</th>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Tracking #</th>
                      <th style={{ padding: '8px', textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wigsNotInPossession
                      .filter(wig => {
                        if (!wigsNotInPossessionSearch.trim()) return true;
                        const search = wigsNotInPossessionSearch.toLowerCase().trim();
                        return (
                          (wig.barcode && wig.barcode.toLowerCase().includes(search)) ||
                          (wig.orderNo && wig.orderNo.toString().toLowerCase().includes(search)) ||
                          (wig.customerName && wig.customerName.toLowerCase().includes(search))
                        );
                      })
                      .map((wig, index) => {
                      const isAlreadyAdded = returnItems.some(item => item.barcode === wig.barcode);
                      return (
                        <tr key={`${wig.barcode}-${index}`} style={{ backgroundColor: index % 2 === 0 ? 'var(--dark-bg)' : 'var(--dark-elevated)' }}>
                          <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontSize: '15px' }}>{wig.barcode}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontSize: '15px' }}>{wig.orderNo}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontSize: '15px' }}>{wig.customerName}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontSize: '15px' }}>{wig.shippedTo || '-'}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontSize: '15px' }}>
                            {wig.shipOutDate ? new Date(wig.shipOutDate).toLocaleDateString() : '-'}
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontSize: '15px' }}>{wig.trackingNumber || '-'}</td>
                          <td style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>
                            <button
                              onClick={() => handleAddFromNotInPossession(wig)}
                              disabled={isAlreadyAdded}
                              style={{
                                padding: '4px 12px',
                                backgroundColor: isAlreadyAdded ? '#95a5a6' : '#27ae60',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: isAlreadyAdded ? 'not-allowed' : 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              {isAlreadyAdded ? 'Added' : 'Add'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* No wigs message */}
          {showWigsNotInPossession && wigsNotInPossession.length === 0 && (
            <div style={{ marginTop: '15px', padding: '20px', textAlign: 'center', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
              <p style={{ color: '#7f8c8d', margin: 0 }}>No wigs currently out for processing this year.</p>
            </div>
          )}
        </div>

              </div>
            )}
          </div>
          {/* End Shipping Grid */}

        </div>
        {/* End Side-by-Side Grids Container */}

        {/* Stock Wig Worksheet Modal */}
        {showWorksheet && selectedWig && (
          <div
            onClick={handleCloseWorksheet}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                maxWidth: '900px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}
            >
              <StockWigWorksheet
                orderNo={selectedWig.orderNo}
                onClose={handleCloseWorksheet}
              />
            </div>
          </div>
        )}

        {/* Shipping Print Modal */}
        {showShippingPrint && selectedShipment && (
          <div
            onClick={() => setShowShippingPrint(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                maxWidth: '700px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}
            >
              <ShippingPrintView
                shipmentId={selectedShipment.shipmentDetails.shipmentId}
                onClose={() => setShowShippingPrint(false)}
              />
            </div>
          </div>
        )}

        {/* Return Shipping Print Modal */}
        {showReturnPrintView && lastCreatedReturnId && (
          <div
            onClick={() => setShowReturnPrintView(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                maxWidth: '700px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}
            >
              <ReturnShippingPrintView
                shipmentId={lastCreatedReturnId}
                onClose={() => setShowReturnPrintView(false)}
              />
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default RachelsRoom;
